import { App } from 'antd';
import { Auth } from 'aws-amplify';
import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import qs from 'query-string';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { API_ERROR_MESSAGES, API_STATUS_CODE } from '@/constants/api.constants';
import { ROUTES_PATH } from '@/constants/routes.constants';
import { getAccessToken } from '@/helpers/auth.helper';
import { trimAllStringEnds } from '@/helpers/string.helper';
import { useModalMethod } from '@/hooks/use-modal-method';
import { useCommonStore } from '@/store/common.store';

const api = axios.create({
  timeout: 30_000,
  withCredentials: true,
  baseURL: import.meta.env.VITE_API_HOST,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': import.meta.env.VITE_API_KEY,
    'Accept-version': import.meta.env.VITE_API_VERSION,
  },
  paramsSerializer: {
    serialize: ({ isFromLogin: _, ...rest }) =>
      qs.stringify(rest, {
        skipNull: true,
        arrayFormat: 'comma',
        skipEmptyString: true,
      }),
  },
});

function AxiosInterceptor() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { pathname, search, state } = useLocation();
  const { error: modalError } = useModalMethod();
  const setIsFormChanged = useCommonStore(state => state.setIsFormChanged);

  useEffect(() => {
    const requestInterceptor = async (request: InternalAxiosRequestConfig) => {
      const { isDownload, isUploadS3 } = request;
      if (isUploadS3) {
        request.baseURL = '';
        request.headers.set(
          'Content-Type',
          'application/x-www-form-urlencoded',
        );
      } else if (!isDownload) {
        const accessToken = await getAccessToken();
        request.headers.set('Authorization', `Bearer ${accessToken}`);
        if (request.method && ['post', 'put'].includes(request.method)) {
          request.data = trimAllStringEnds(request.data);
        }
      }
      return request;
    };
    const requestErrorInterceptor = (error: AxiosError) =>
      Promise.reject(error);

    const responseInterceptor = (response: AxiosResponse) => response;
    const responseErrorInterceptor = async (error: AxiosError) => {
      const {
        isDownload,
        selfHandleError,
        method: errorMethod,
      } = error?.config ?? {};
      const errorStatus = isDownload
        ? API_STATUS_CODE.downloadFail
        : ((error?.response?.status ?? error?.code) as number);
      const errorMessage = (error?.response?.data as { message: string })
        ?.message;
      const errorContent = globalThis.navigator.onLine
        ? API_ERROR_MESSAGES[errorStatus] || errorMessage
        : API_ERROR_MESSAGES.noInternet;
      const loginRedirectUrl = `${ROUTES_PATH.login}?redirect_url=${pathname}${search}`;

      if (selfHandleError) throw error;

      switch (errorStatus) {
        case API_STATUS_CODE.outdated: {
          modalError({
            okText: '再読み込み',
            content: errorContent,
            onOk() {
              globalThis.location.reload();
            },
          });
          break;
        }
        case API_STATUS_CODE.unauthorized: {
          setIsFormChanged(false);
          modalError({
            content: errorContent,
            onOk: async () => {
              await Auth.signOut();
              navigate(loginRedirectUrl);
            },
          });
          break;
        }
        case API_STATUS_CODE.notFound: {
          if (errorMethod === 'get') {
            setIsFormChanged(false);
            modalError({
              content: errorContent,
              onOk() {
                const redirectRoute = state?.isFromLogin
                  ? ROUTES_PATH.root
                  : -1;
                navigate(redirectRoute as string);
              },
            });
          } else {
            message.error(errorContent);
          }
          break;
        }
        case API_STATUS_CODE.movedPermanently: {
          setIsFormChanged(false);
          modalError({
            content: errorContent,
            onOk: async () => {
              await Auth.signOut();
              navigate(loginRedirectUrl);
            },
          });
          break;
        }
        default: {
          message.error(errorContent);
          break;
        }
      }
      throw error;
    };

    const requestInterceptors = api.interceptors.request.use(
      requestInterceptor,
      requestErrorInterceptor,
    );
    const responseInterceptors = api.interceptors.response.use(
      responseInterceptor,
      responseErrorInterceptor,
    );

    return () => {
      api.interceptors.request.eject(requestInterceptors);
      api.interceptors.response.eject(responseInterceptors);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // eslint-disable-next-line unicorn/no-null
  return null;
}

// eslint-disable-next-line react-refresh/only-export-components
export { api, AxiosInterceptor };
